import { CallingHistoryLog } from "../../../../pages/callingWorklist/services/callingHistoryService";
import { buildHistoryFieldRows } from "../utils/buildHistoryFields";
import { CallSummaryMode } from "../utils/callSummaryMode";
import { formatHistoryTimestamp } from "../utils/formatHistoryTimestamp";
import HistoryEntryDeleteControl from "./historyEntryDeleteControl";

interface HistoryEntryCardProps {
  entry: CallingHistoryLog;
  /** Row's current second STP status, used for entries saved without it. */
  fallbackSecondStpStatus: string | null;
  /** Omitted for entries that cannot be traced back to a Firestore document. */
  onDelete?: () => void;
  isDeleting?: boolean;
}

/**
 * One saved calling or fax summary: a header with the time it was recorded and
 * an optional delete control, two-column field rows split by dashed rules, and
 * the comment block.
 *
 * @param props.entry - History entry to render
 * @param props.fallbackSecondStpStatus - Fallback for legacy entries
 * @param props.onDelete - Deletes this entry; the control is hidden when absent
 * @param props.isDeleting - True while this entry's delete is in flight
 */
const HistoryEntryCard = (props: HistoryEntryCardProps) => {
  const { entry } = props;
  const rows = buildHistoryFieldRows(entry, props.fallbackSecondStpStatus);
  const heading = entry.mode === CallSummaryMode.FAX ? "Faxed on" : "Called on";
  const comment = entry.comment?.trim() ?? "";

  return (
    <li className="list-none overflow-hidden rounded-lg border border-primaryGray-15 bg-white">
      <div className="flex items-center justify-between gap-3 border-b border-primaryGray-15 bg-primaryGray-16 px-4 py-3">
        <span className="text-small font-bold text-primaryGray-6">
          {heading}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-tiny text-primaryGray-1">
            {formatHistoryTimestamp(entry)}
          </span>
          {props.onDelete && (
            <HistoryEntryDeleteControl
              onDelete={props.onDelete}
              isDeleting={props.isDeleting ?? false}
            />
          )}
        </div>
      </div>

      <div className="px-4">
        {rows.map((row, rowIndex) => (
          <div
            key={row.map((field) => field.label).join("-")}
            className={`grid grid-cols-2 gap-4 py-3 ${
              rowIndex < rows.length - 1
                ? "border-b border-dashed border-primaryGray-14"
                : ""
            }`}
          >
            {row.map((field) => (
              <div key={field.label} className="flex min-w-0 flex-col gap-1">
                <span className="text-small font-semibold text-primaryGray-6">
                  {field.label}
                </span>
                <span className="break-words text-tiny leading-h11 text-primaryGray-1">
                  {field.value}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {entry.attachment_url && (
        <div className="flex flex-col gap-1 border-t border-primaryGray-15 px-4 py-3">
          <span className="text-small font-semibold text-primaryGray-6">
            Document
          </span>
          <a
            href={entry.attachment_url}
            target="_blank"
            rel="noreferrer"
            className="truncate text-tiny font-semibold text-blue-600 underline"
          >
            {entry.attachment_name || "View document"}
          </a>
        </div>
      )}

      {comment && (
        <div className="flex flex-col gap-1 border-t border-primaryGray-15 bg-primaryGray-16 px-4 py-3">
          <span className="text-small font-semibold text-primaryGray-9">
            Comment
          </span>
          <span className="whitespace-pre-wrap break-words text-small text-primaryGray-1">
            {comment}
          </span>
        </div>
      )}
    </li>
  );
};

export default HistoryEntryCard;
